import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getLokerDetail, applyJob } from '../services/api';
import { 
  ArrowLeft, 
  X, 
  CheckCircle, 
  Loader2, 
  MessageCircle, 
  Mail, 
  FileText, 
  ChevronRight,
  Share2,
  Clock,
  MapPin,
  Briefcase
} from 'lucide-react';
import { useNotification } from '../context/NotificationContext';

const BloombergButton = ({ children, onClick, className = '', variant = 'primary', disabled = false }) => {
    const base = "px-6 py-4 text-sm font-bold uppercase tracking-widest transition-all duration-200 border flex items-center justify-center gap-2";
    const styles = {
      primary: "bg-black text-white border-black hover:bg-zinc-800 disabled:opacity-70 disabled:cursor-not-allowed",
      outline: "bg-transparent text-black border-zinc-200 hover:border-black hover:bg-black hover:text-white",
    };
    
    return (
      <button onClick={onClick} disabled={disabled} className={`${base} ${styles[variant]} ${className}`}>
        {children}
      </button>
    );
};

const SectionTag = ({ children }) => (
    <div className="flex items-center gap-2 mb-6">
      <div className="w-8 h-[2px] bg-blue-600"></div>
      <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-blue-600 italic">
        {children}
      </span>
    </div>
);

const KarirDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  
  // Apply Form State
  const [applyForm, setApplyForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    cv: null
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchJob = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getLokerDetail(id);
        if (data) {
          setJob(data);
        } else {
          setError('Lowongan tidak ditemukan atau sudah ditutup.');
        }
      } catch (err) {
        setError('Terjadi kesalahan saat memuat lowongan.');
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
    window.scrollTo(0, 0);
  }, [id]);

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    
    if (!applyForm.cv) {
      addNotification('Mohon upload CV kamu (PDF/DOC).', 'error');
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append('job_id', id);
    formData.append('name', applyForm.name);
    formData.append('email', applyForm.email);
    formData.append('phone', applyForm.phone);
    formData.append('message', applyForm.message);
    formData.append('cv', applyForm.cv);

    try {
      await applyJob(formData);
      addNotification('Lamaran berhasil dikirim!', 'success');
      setShowApplyModal(false);
      setApplyForm({ name: '', email: '', phone: '', message: '', cv: null });
    } catch (err) {
      console.error(err);
      addNotification(err.response?.data?.message || 'Gagal mengirim lamaran.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWhatsAppApply = () => {
    const message = `Halo iMoonusel, saya tertarik melamar posisi *${job.title}*. Mohon info lebih lanjut.`;
    window.open(`https://wa.me/628987459759?text=${encodeURIComponent(message)}`, '_blank');
    setShowChoiceModal(false);
  };

  const handleEmailApply = () => {
    const subject = `Lamaran Pekerjaan: ${job.title}`;
    const body = `Halo Tim HR iMoonusel,\n\nSaya ingin melamar untuk posisi ${job.title}.\n\n(Mohon lampirkan CV dan Portofolio Anda disini)`;
    window.open(`mailto:imoonuselkonveksi@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
    setShowChoiceModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white p-6 text-center">
        <h2 className="text-2xl font-bold mb-4">{error || 'Lowongan tidak ditemukan'}</h2>
        <BloombergButton onClick={() => navigate('/karir')}>Back to Careers</BloombergButton>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-black font-sans selection:bg-blue-600 selection:text-white">
      
      {/* Back Nav */}
      <div className="fixed top-24 left-6 z-20 hidden lg:block">
          <button 
            onClick={() => navigate('/karir')}
            className="w-12 h-12 flex items-center justify-center border border-zinc-200 bg-white hover:bg-black hover:text-white transition-all rounded-full shadow-sm"
          >
              <ArrowLeft size={18} />
          </button>
      </div>

      <div className="max-w-4xl mx-auto px-6 pt-32 pb-24">
         
         <div className="mb-12 border-b border-black pb-12">
             <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
                <div>
                    <SectionTag>{job.position || 'Open Position'}</SectionTag>
                    <h1 className="text-5xl md:text-6xl font-black tracking-tighter leading-[0.9] uppercase">
                        {job.title}
                    </h1>
                </div>
                <div className="flex gap-2">
                    <button className="p-3 border border-zinc-200 hover:border-black transition-colors rounded-full">
                        <Share2 size={16} />
                    </button>
                </div>
             </div>

             <div className="flex flex-wrap gap-6 text-xs font-bold uppercase tracking-widest text-zinc-500">
                <div className="flex items-center gap-2">
                    <Briefcase size={14} /> {job.job_type}
                </div>
                <div className="flex items-center gap-2">
                    <MapPin size={14} /> Bandung, ID
                </div>
                <div className="flex items-center gap-2">
                    <Clock size={14} /> Posted: {new Date(job.created_at).toLocaleDateString()}
                </div>
             </div>
         </div>

         <div className="prose prose-lg prose-zinc max-w-none">
            {/* Using dangerouslySetInnerHTML assuming content is safe from backend */}
            <div className="mb-12">
                <h3 className="text-2xl font-bold tracking-tight mb-6 uppercase">Description</h3>
                <div className="text-zinc-600 font-light leading-relaxed whitespace-pre-line text-lg" dangerouslySetInnerHTML={{ __html: job.description }}></div>
            </div>

            {job.requirements && (
                <div className="bg-zinc-50 p-8 border-l-4 border-blue-600">
                    <h3 className="text-xl font-bold tracking-tight mb-6 uppercase">Requirements</h3>
                    <div className="text-zinc-700 font-medium leading-relaxed whitespace-pre-line" dangerouslySetInnerHTML={{ __html: job.requirements }}></div>
                </div>
            )}
         </div>

         {/* Fixed Bottom Action on Mobile, Static on Desktop */}
         <div className="fixed bottom-0 left-0 w-full p-4 bg-white border-t border-zinc-200 md:static md:bg-transparent md:border-none md:p-0 md:mt-16 z-40">
            <div className="max-w-4xl mx-auto flex flex-col md:flex-row gap-4">
                <BloombergButton onClick={() => setShowChoiceModal(true)} className="w-full md:w-auto flex-1 text-lg py-4">
                    Apply For Position
                </BloombergButton>
                <BloombergButton variant="outline" onClick={() => navigate('/karir')} className="hidden md:flex">
                    Back to List
                </BloombergButton>
            </div>
         </div>

      </div>

      {/* Choice Modal */}
      {showChoiceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowChoiceModal(false)}>
          <div className="bg-white max-w-md w-full p-8 shadow-2xl animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
             <div className="flex justify-between items-center mb-8">
                <div>
                    <h3 className="text-xl font-bold uppercase tracking-tight">Apply Now</h3>
                    <p className="text-xs text-zinc-400 font-bold uppercase tracking-widest mt-1">Select Method</p>
                </div>
                <button onClick={() => setShowChoiceModal(false)} className="hover:text-red-600 transition-colors"><X size={24} /></button>
             </div>
             
             <div className="flex flex-col gap-3">
                <button 
                    className="flex items-center justify-between p-4 border border-zinc-200 hover:border-blue-600 hover:bg-blue-50 transition-all group text-left"
                    onClick={() => {setShowChoiceModal(false); setShowApplyModal(true);}}
                >
                   <div className="flex items-center gap-4">
                       <div className="w-10 h-10 bg-zinc-100 flex items-center justify-center rounded-full group-hover:bg-blue-200 transition-colors text-zinc-600 group-hover:text-blue-700">
                           <FileText size={20} />
                       </div>
                       <div>
                          <p className="font-bold text-sm">Website Form</p>
                          <p className="text-xs text-zinc-500">Direct application upload</p>
                       </div>
                   </div>
                   <ChevronRight size={16} className="text-zinc-400 group-hover:text-blue-600" />
                </button>

                <button 
                    className="flex items-center justify-between p-4 border border-zinc-200 hover:border-green-600 hover:bg-green-50 transition-all group text-left"
                    onClick={handleWhatsAppApply}
                >
                   <div className="flex items-center gap-4">
                       <div className="w-10 h-10 bg-zinc-100 flex items-center justify-center rounded-full group-hover:bg-green-200 transition-colors text-zinc-600 group-hover:text-green-700">
                           <MessageCircle size={20} />
                       </div>
                       <div>
                          <p className="font-bold text-sm">WhatsApp</p>
                          <p className="text-xs text-zinc-500">Quick chat with HR</p>
                       </div>
                   </div>
                   <ChevronRight size={16} className="text-zinc-400 group-hover:text-green-600" />
                </button>

                <button 
                    className="flex items-center justify-between p-4 border border-zinc-200 hover:border-purple-600 hover:bg-purple-50 transition-all group text-left"
                    onClick={handleEmailApply}
                >
                   <div className="flex items-center gap-4">
                       <div className="w-10 h-10 bg-zinc-100 flex items-center justify-center rounded-full group-hover:bg-purple-200 transition-colors text-zinc-600 group-hover:text-purple-700">
                           <Mail size={20} />
                       </div>
                       <div>
                          <p className="font-bold text-sm">Email</p>
                          <p className="text-xs text-zinc-500">Send formal cover letter</p>
                       </div>
                   </div>
                   <ChevronRight size={16} className="text-zinc-400 group-hover:text-purple-600" />
                </button>
             </div>
          </div>
        </div>
      )}

      {/* Apply Form Modal */}
      {showApplyModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md" onClick={() => setShowApplyModal(false)}>
              <div className="bg-white max-w-lg w-full p-8 shadow-2xl relative animate-in slide-in-from-bottom-8 duration-300" onClick={e => e.stopPropagation()}>
                  
                  <button className="absolute top-6 right-6 hover:text-red-600 transition-colors" onClick={() => setShowApplyModal(false)}>
                      <X size={24} />
                  </button>

                  <h2 className="text-2xl font-black uppercase tracking-tight mb-2">Submit Application</h2>
                  <p className="text-sm text-zinc-500 mb-8 font-medium">Position: <span className="text-blue-600">{job.title}</span></p>

                  <form onSubmit={handleApplySubmit} className="space-y-4">
                      <div>
                          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Full Name</label>
                          <input 
                              type="text" 
                              required 
                              className="w-full border-b border-zinc-300 py-2 focus:outline-none focus:border-black transition-colors font-medium bg-transparent"
                              placeholder="Enter your full name"
                              value={applyForm.name}
                              onChange={e => setApplyForm({...applyForm, name: e.target.value})}
                          />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Email</label>
                            <input 
                                type="email" 
                                required 
                                className="w-full border-b border-zinc-300 py-2 focus:outline-none focus:border-black transition-colors font-medium bg-transparent"
                                placeholder="name@example.com"
                                value={applyForm.email}
                                onChange={e => setApplyForm({...applyForm, email: e.target.value})}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">WhatsApp / Phone</label>
                            <input 
                                type="tel" 
                                required 
                                className="w-full border-b border-zinc-300 py-2 focus:outline-none focus:border-black transition-colors font-medium bg-transparent"
                                placeholder="08..."
                                value={applyForm.phone}
                                onChange={e => setApplyForm({...applyForm, phone: e.target.value})}
                            />
                        </div>
                      </div>

                      <div>
                          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">CV / Portfolio (PDF/DOC)</label>
                          <div className="relative">
                            <input 
                                type="file" 
                                required 
                                accept=".pdf,.doc,.docx"
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                onChange={e => setApplyForm({...applyForm, cv: e.target.files[0]})}
                            />
                            <div className={`w-full border border-dashed ${applyForm.cv ? 'border-green-500 bg-green-50' : 'border-zinc-300 bg-zinc-50'} py-4 text-center text-sm font-bold text-zinc-400 pointer-events-none transition-colors`}>
                                {applyForm.cv ? (
                                    <span className="text-green-600 flex items-center justify-center gap-2">
                                        <CheckCircle size={16} /> {applyForm.cv.name}
                                    </span>
                                ) : (
                                    "Click to Upload File"
                                )}
                            </div>
                          </div>
                      </div>

                      <div className="pt-2">
                          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Additional Note</label>
                          <textarea 
                              rows="2"
                              className="w-full border-b border-zinc-300 py-2 focus:outline-none focus:border-black transition-colors font-medium bg-transparent resize-none"
                              placeholder="Tell us why you fit this role..."
                              value={applyForm.message}
                              onChange={e => setApplyForm({...applyForm, message: e.target.value})}
                          />
                      </div>

                      <div className="pt-4">
                          <BloombergButton 
                              variant="primary" 
                              className="w-full justify-center"
                              disabled={submitting}
                          >
                              {submitting ? <><Loader2 className="animate-spin" size={20} /> Processing...</> : 'Send Application'}
                          </BloombergButton>
                      </div>
                  </form>
              </div>
          </div>
      )}

    </div>
  );
};

export default KarirDetail;
